export const SOLUTIONS_DISCLAIMER =
  "Availability, savings and eligibility for any solar model depend on project economics, applicable central and state regulations, location, grid connectivity and each consumer's specific requirements. Solitaire India does not guarantee savings, approvals or outcomes. Every opportunity is evaluated case by case.";

export const SOLUTIONS = [
  {
    id: "rooftop",
    n: "01",
    title: "Rooftop Solar",
    kicker: "On-site generation",
    image: "/img/plant.jpg",
    summary:
      "Solar panels installed on factory sheds, warehouses, commercial buildings or carports, supplying electricity directly to the premises where it is consumed.",
    points: [
      "Uses existing roof or open space within your premises",
      "Generation is consumed on site, reducing electricity drawn from the grid",
      "Can be structured under CAPEX or OPEX / RESCO models",
    ],
    detail: [
      "Rooftop solar is usually the first option a business evaluates because the plant sits within its own premises and does not depend on transmitting power through the grid. Factory sheds, warehouse roofs, RCC terraces, car parks and open land inside the boundary wall can all be considered, provided they are structurally sound and largely free from shading.",
      "Electricity generated during the day is consumed directly by the facility, offsetting part of the power that would otherwise be drawn from the distribution company. Any excess may be exported to the grid where metering arrangements allow. Because output is limited by usable area, rooftop solar often meets only a portion of an energy-intensive facility's demand, and is frequently combined with an off-site option.",
    ],
    steps: [
      "Site survey of roof area, structure, shading and electrical infrastructure",
      "Sizing against consumption pattern, sanctioned load and metering rules",
      "Choosing between CAPEX ownership or an OPEX / RESCO agreement",
      "Installation, grid synchronisation approvals and commissioning",
    ],
    suits: "Manufacturing units, warehouses, cold storage, commercial complexes, hospitals and institutions with suitable roof or open space.",
    considerations: "Roof condition, structural capacity, shading, available area, connected load and applicable net-metering or gross-metering rules.",
    models: ["CAPEX", "OPEX / RESCO"],
  },
  {
    id: "open-access",
    n: "02",
    title: "Open Access Solar",
    kicker: "Off-site generation",
    image: "/img/farm.jpg",
    summary:
      "Electricity generated at a solar plant located away from your facility and transmitted to your premises through the grid, under the open access framework.",
    points: [
      "Suited to consumers whose demand exceeds what their rooftop can supply",
      "Power is wheeled through transmission and distribution networks",
      "Charges, surcharges and banking terms vary by state",
    ],
    detail: [
      "Open access lets an eligible consumer procure electricity from a generator other than its local distribution company. The solar plant is built at a location chosen for land availability and solar resource, often in a different district, and the power is injected into the grid and delivered to the consumer's connection point.",
      "Because it is not constrained by roof area, open access can make much larger volumes of solar power available to a facility. In exchange, the arrangement is shaped by regulation: eligibility thresholds, transmission and wheeling charges, applicable surcharges, banking of surplus units and scheduling requirements are set by central rules and State Electricity Regulatory Commissions, and can change over time.",
    ],
    steps: [
      "Check eligibility based on connection, load and current state regulations",
      "Select the structure: third-party, captive or group captive",
      "Evaluate developers, landed cost of power and contract terms",
      "Obtain open access approvals, then commission and begin supply",
    ],
    suits: "High-tension (HT) consumers with steady, significant daytime or round-the-clock demand that rooftop alone cannot meet.",
    considerations: "Eligibility thresholds, connection voltage, state open access regulations, applicable charges and surcharges, and banking provisions.",
    models: ["Third-party", "Captive", "Group captive"],
  },
  {
    id: "third-party",
    n: "03",
    title: "Third-Party Open Access",
    kicker: "Buy from a developer",
    image: "/img/park.jpg",
    summary:
      "A developer owns and operates an off-site solar plant and sells electricity to your business under a long-term Power Purchase Agreement (PPA).",
    points: [
      "The consumer typically has no ownership in the generating asset",
      "Tariff, tenure and obligations are defined in the PPA",
      "Developer is responsible for building and operating the plant",
    ],
    detail: [
      "In third-party open access, a solar developer or independent power producer finances, builds, owns and operates the plant. Your business simply buys the electricity it generates at an agreed tariff, delivered through the grid under the open access framework.",
      "This keeps capital free for core operations and places construction and operating responsibility with the developer. The commercial outcome depends heavily on the Power Purchase Agreement: tariff and any escalation, tenure, minimum offtake, what happens if regulations or charges change, and how the agreement can be exited or transferred.",
    ],
    steps: [
      "Define volume, tenure and preferred tariff structure",
      "Invite and compare developer proposals on a like-for-like basis",
      "Negotiate the PPA alongside your legal advisors",
      "Developer builds, obtains approvals and starts supply",
    ],
    suits: "Businesses that want off-site solar without investing capital or holding equity in a generating asset.",
    considerations: "PPA tenure, tariff structure, exit clauses, state-specific charges and the developer's track record.",
    models: ["OPEX / PPA"],
  },
  {
    id: "captive",
    n: "04",
    title: "Captive Solar",
    kicker: "Own your generation",
    image: "/img/commercial.jpg",
    summary:
      "A solar plant set up primarily for the consumer's own use, where the consumer holds ownership and consumption in line with the captive criteria under applicable electricity rules.",
    points: [
      "Consumer holds equity in the generating company or asset",
      "Captive status depends on meeting prescribed ownership and consumption criteria",
      "May carry different treatment of certain charges, subject to regulations",
    ],
    detail: [
      "A captive solar plant is set up primarily for the consumer's own use. The consumer invests in the generating asset, either directly or through a company in which it holds the required equity, and consumes the required share of the electricity it produces, as defined under applicable electricity rules.",
      "Captive arrangements give the consumer ownership and long-term control over its power source, and may receive different regulatory treatment of certain charges compared with other open access routes. Captive status must be maintained over time, so ownership, consumption and documentation need to be planned and monitored from the start.",
    ],
    steps: [
      "Assess capital availability and the long-term site commitment",
      "Structure ownership in line with captive requirements",
      "Select EPC and O&M partners and finalise the plant location",
      "Commission the plant and maintain annual compliance",
    ],
    suits: "Large consumers with available capital that want ownership and control over a dedicated source of solar power.",
    considerations: "Ownership structure, ongoing compliance with captive criteria, capital commitment and state regulatory treatment.",
    models: ["CAPEX", "Equity participation"],
  },
  {
    id: "group-captive",
    n: "05",
    title: "Group Captive Solar",
    kicker: "Shared ownership",
    image: "/img/hero.jpg",
    summary:
      "Several consumers jointly hold equity in a solar generating company, typically a special purpose vehicle, and consume power from it in proportion to their participation.",
    points: [
      "Allows multiple businesses to participate in one larger project",
      "Each consumer holds a smaller equity stake than a standalone captive",
      "Requires careful structuring and ongoing compliance monitoring",
    ],
    detail: [
      "Group captive extends the captive concept to several consumers. A developer typically sets up a special purpose vehicle (SPV) that owns the solar plant, and each participating consumer acquires a stake in that SPV and consumes power from it in line with the applicable proportionality requirements.",
      "This allows businesses to access captive-style arrangements with a smaller individual investment than building a plant alone, while the developer usually handles construction and operations. Because several shareholders are involved, the shareholder agreement, the PPA with the SPV and the annual verification of captive status all require careful structuring.",
    ],
    steps: [
      "Confirm consumption profile and suitability for group captive",
      "Evaluate the developer, SPV structure and equity requirement",
      "Sign shareholder and power purchase agreements with the SPV",
      "Monitor consumption and compliance through the project life",
    ],
    suits: "Mid to large C&I consumers seeking captive-style benefits without setting up and owning an entire plant.",
    considerations: "SPV structure, proportionate consumption requirements, shareholder agreements and annual compliance verification.",
    models: ["Equity in SPV", "PPA with SPV"],
  },
  {
    id: "hybrid",
    n: "06",
    title: "Wind-Solar Hybrid",
    kicker: "Round-the-clock blend",
    image: "/img/hybrid.jpg",
    summary:
      "Electricity from wind and solar generation combined into one supply, so that the two sources together cover more hours of the day than solar alone.",
    detail: [
      "Solar generates during daylight hours, while wind often blows more strongly in the evening, at night and during the monsoon months. A hybrid project combines both, either at a single co-located site or from separate plants supplying the same consumer, to create a flatter and more consistent generation profile.",
      "For consumers with round-the-clock or multi-shift operations, this can increase the share of renewable electricity they are able to use. Hybrid supply is usually procured through open access under third-party, captive or group captive structures, and its feasibility depends on wind resource, land, transmission availability and state regulations. Battery storage may also be considered to further shape supply.",
    ],
    steps: [
      "Analyse hourly and seasonal consumption across shifts",
      "Assess the wind-solar mix and possible storage needs",
      "Select developers and the open access structure",
      "Approvals, commissioning and blended supply to your facility",
    ],
    suits: "Facilities running multiple shifts or continuous processes that want renewable supply beyond daylight hours.",
    points: [
      "Combines complementary wind and solar generation profiles",
      "Can cover more of a facility's evening and night-time demand",
      "Procured through open access structures, subject to regulations",
    ],
    considerations: "Wind resource at the site, land and transmission availability, state hybrid and open access policies, applicable charges, and the consumer's hourly load profile.",
    models: ["Third-party", "Captive", "Group captive"],
  },
];

export const MODELS = {
  capex: {
    id: "capex",
    label: "CAPEX",
    title: "CAPEX model",
    lead: "Your business invests in and owns the solar plant.",
    rows: [
      ["Ownership", "Consumer owns the asset"],
      ["Upfront investment", "Required from the consumer"],
      ["Operations & maintenance", "Managed by the consumer, usually through an O&M contract"],
      ["Payment for power", "No per-unit payment to a developer"],
      ["Typically considered by", "Businesses with available capital and a long-term site commitment"],
      ["Key questions", "Capital allocation, payback expectations, tax treatment (with your advisors), O&M arrangements"],
    ],
  },
  opex: {
    id: "opex",
    label: "OPEX / RESCO",
    title: "OPEX / RESCO model",
    lead: "A developer (Renewable Energy Service Company) invests in and owns the plant; you pay for the electricity.",
    rows: [
      ["Ownership", "Developer owns the asset"],
      ["Upfront investment", "Typically none from the consumer"],
      ["Operations & maintenance", "Developer's responsibility under the agreement"],
      ["Payment for power", "Per-unit tariff defined in a long-term PPA"],
      ["Typically considered by", "Businesses preferring to preserve capital for core operations"],
      ["Key questions", "Tariff structure, PPA tenure, minimum offtake, exit and transfer terms"],
    ],
  },
};
